/* eslint-disable camelcase */
import React, { useState, useEffect, useMemo } from "react";
import ReactSelect from "react-select";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { ErrorMessage } from "@hookform/error-message";
import styled, { css } from "styled-components";
import { useScreenClass } from "react-grid-system";
import MinimalSelect from "./MinimalSelect";
import FlexBox, { FlexGrow } from "./FlexBox";
import theme from "../styles/theme";
import Card from "./Card";
import { StyledText, TextStatus } from "./Text";
import Input from "./Input";
import TextArea from "./TextArea";
import Button from "./Button";
import { RightAlign } from "./UtilsComponents";
import Icon, { ClickableIcon } from "./Icon";
import Status from "../types/enum/Status";
import Modal from "./Modal";
import convertToSelect from "../hooks/convertToSelect";
import SnackBar from "./SnackBar";
import Table from "./Table";
import NullStateTR from "./NullStateTr";
import Upload from "./Upload";

const sharedMinimalStyle = css`
  width: 100%;
  border-color: hsl(0, 0%, 80%);
  border-radius: 4px;
  border-style: solid;
  border-width: 1px;
  padding: 0px 12px;
  .react-select__menu {
    width: 100%;
    min-width: unset;
    margin-left: -12px;
  }
`;
const StyledMinimalSelect = styled(MinimalSelect)`
  ${sharedMinimalStyle}
`;
type EditAliasForm = {
  source_program: string;
  alias_program: string;
  alias_course_code: string;
};
type CourseEditModalProps = {
  isOpen?: boolean;
  setOpen?: (x: boolean) => void;
  currentCourse: {
    [key: string]: any;
  };
  isAliasDeleteOpen?: boolean;
  setIsAliasDeleteOpen: (x: boolean) => void;
  setCurrentAlias: (x: string) => void;
  onEditComplete?: () => void;
};

const AliasEditModal = ({
  isOpen,
  setOpen,
  currentCourse,
  onEditComplete,
  setIsAliasDeleteOpen,
  setCurrentAlias,
  isAliasDeleteOpen,
}: CourseEditModalProps): JSX.Element => {
  const validationSchema = Yup.object({
    source_program: Yup.string().required("Source Program is required"),
    alias_program: Yup.string().required("Alias Program is required"),
    alias_course_code: Yup.string().required("Alias Course Code is required"),
  });
  const {
    register,
    handleSubmit,

    watch,
    formState: { errors, isDirty },
  } = useForm<EditAliasForm>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      source_program: "",
      alias_program: "",
      alias_course_code: "",
    },
  });
  const screenClass = useScreenClass();
  const isMobile = useMemo(() => ["xs", "sm"].includes(screenClass), [
    screenClass,
  ]);
  const [loading, setLoading] = useState<boolean>(true);
  const watchAllFields = watch();
  const history = useHistory();
  const programsReg = register("source_program");
  const [errorSnackbarOpen, setErrorSnackbarOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState("")
  const [aliasData, setAliasData] = useState<any>([]);
  const [newAliasData, setNewAliasData] = useState<any>({
    course_master_id: currentCourse?.id,
    source_program_id: "",
    course_master_alias_id: "",
    alias_program_id: "",
  });
  const [sourceProgramOption, setSourceProgramOption] = useState<any>([
    {
      label: "",
      value: "",
    },
  ]);
  const [aliasCourseOptions, setAliasCourseOptions] = useState<any>([]);
  const [aliasProgramOptions, setAliasProgramOptions] = useState<any>([
    { label: "", value: "" },
  ]);
  const [uploadedFile, setUploadedFile] = useState<any>();
  const [isOpenError, setOpenError] = useState<boolean>(false);

  const currentAliasSection = (
    <>
      {aliasData.map((alias: any) => {
        return (
          <tr>
            <td>{alias.source_program_name}</td>
            <td>{alias.course_master_alias_code}</td>
            <td>{alias.alias_program_name}</td>
            <td>
              <Icon
                onClick={() => {
                  setIsAliasDeleteOpen(true);
                  setCurrentAlias(alias);
                }}
                style={{ color: "red", cursor: "pointer" }}
                name="trash"
              />
            </td>
          </tr>
        );
      })}

      {!aliasData?.length && <NullStateTR>No sections info.</NullStateTR>}
    </>
  );

  useEffect(() => {
    fetchPrograms();
    fetchAliasData();
    setInitialValues();
  }, []);

  const setInitialValues = () => {
    const tempData = currentCourse?.programs?.map((program: any) => {
      return {
        label: program.name,
        value: program.id,
      };
    });
    setSourceProgramOption(tempData);
  };
  const handleAliasProgramChange = (program: any, dirty: boolean) => {
    setNewAliasData({
      ...newAliasData,
      alias_program_id: program.value,
      course_master_alias_id: "",
    });
    axios
      .get(`/api/v1/courses/programs/${program.value}`)
      .then((res) => {
        const tempData = res.data.data.courses?.map((course: any) => {
          return {
            label: course.code + " (" + course.units + ")",
            value: course.id,
          };
        });
        setAliasCourseOptions(tempData);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleAliasUpload = (file: any) => {
    const formData = new FormData();
    formData.append('file', file);
    const config = {
      headers: {
        'content-type': 'multipart/form-data',
      },
    };
     axios
      .post('/api/v1/courses/alias/upload', formData, config)
      .then((res) => {
        setLoading(false);
       fetchAliasData();
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        // setIsErrorOpen(true);
      });
  }

  const handleAddAlias = () => {
    axios
      .post("/api/v1/courses/alias/new", newAliasData)
      .then((res) => {
        fetchAliasData();
        setNewAliasData({
          course_master_id: currentCourse?.id,
          source_program_id: "",
          course_master_alias_id: "",
          alias_program_id: "",
        });
        setAliasCourseOptions([]);
        fetchPrograms();
      })
      .catch((error) => {
        setOpenError(true);
        setErrorMessage(error.response.data.error);
        setErrorSnackbarOpen(true);
        setLoading(false);
      });
  };
  const fetchPrograms = () => {
    setLoading(true);
    axios
      .get("/api/v1/courses/programs")
      .then((res) => {
        if (res.data.data.programs) {
          // eslint-disable-next-line max-len
          const programList: React.SetStateAction<
            { label: string; value: string }[]
          > = [];
          res.data.data.programs.forEach(
            (element: { short_name: string; id: string }) => {
              const array = { label: "", value: "" };
              array.label = element.short_name;
              array.value = element.id;
              programList.push(array);
            }
          );
          setAliasProgramOptions(programList);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };
  const fetchAliasData = () => {
    setLoading(true);
    axios
      .get(`/api/v1/courses/alias?course_master_id=${currentCourse.id}`)
      .then((res) => {
        if (res.data.course_alias) {
          setAliasData(res.data.course_alias);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };
  const onSubmitClicked = (data: any) => {
    setLoading(true);
    axios
      .put(`/api/v1/course-master/${currentCourse.id}`, data)
      .then((res) => {
        setLoading(false);
        onEditComplete?.();
      })
      .catch((error) => {
        setOpenError(true);
        console.log(error);
        setErrorSnackbarOpen(true);
        setLoading(false);
      });
  };
  const onError = (errors: any, e: any) => console.log("error", errors, e);
  const onErrorCloseModal = () => {
    setOpenError(false);
  };

  console.log("currentCourse", uploadedFile);

  return (
    <>
      <SnackBar
        isOpen={errorSnackbarOpen}
        setOpen={setErrorSnackbarOpen}
        type="error"
        message={errorMessage || "Something went wrong. Please try again!"}
      />

      <Modal
        isOpen={!!isOpen}
        setOpen={setOpen}
        customStyle={{
          content: { width: "100%", maxWidth: isMobile ? "100%" : "600px" },
        }}
      >
        <FlexBox
          style={{
            marginBottom: theme.spacing.base * 2,
          }}
        >
          <FlexGrow>
            <h3 style={{ marginRight: "auto", marginBottom: 0 }}>
              Course Aliases for {currentCourse.code} ({currentCourse.units})
            </h3>
          </FlexGrow>
          <RightAlign>
            <ClickableIcon name="x-s" onClick={() => setOpen?.(false)} />
          </RightAlign>
        </FlexBox>
        {/* <StyledText color={"#000000"} fontWeight={"bold"}>
          Source Code {currentCourse.code}
        </StyledText> */}
        {!loading ? (
          <form onSubmit={handleSubmit(onSubmitClicked, onError)}>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
                marginTop: 40,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Source Program
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <StyledMinimalSelect
                    // multi
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Find programs..."
                    options={sourceProgramOption}
                    onChange={(e: any) => {
                      setNewAliasData({
                        ...newAliasData,
                        source_program_id: e.value,
                        alias_program_id:
                          e.value === newAliasData.alias_program_id
                            ? ""
                            : newAliasData.alias_program_id,
                      });

                      // Re-add the previously selected option to aliasProgramOptions
                      setAliasProgramOptions((prevOptions: any[]) => {
                        const previousOption = sourceProgramOption.find(
                          (option: any) =>
                            option.value === newAliasData.source_program_id
                        );
                        const updatedOptions = previousOption
                          ? [...prevOptions, previousOption]
                          : prevOptions;

                        // Remove the newly selected option
                        return updatedOptions.filter(
                          (option) => option.value !== e.value
                        );
                      });
                    }}
                    defaultValue={""}
                    onBlur={programsReg.onBlur}
                    innerRef={programsReg.ref}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>
            <FlexBox
              alignItems="center"
              style={{
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow grow={1}>
                <StyledText color={"#000000"} fontWeight={"bold"}>
                  Alias Program
                </StyledText>
              </FlexGrow>
              <FlexGrow grow={2}>
                <>
                  <StyledMinimalSelect
                    // multi
                    value={
                      aliasProgramOptions.find(
                        (v: any) => v.value === newAliasData.alias_program_id
                      ) || null
                    }
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                    }}
                    placeholder="Find programs..."
                    options={aliasProgramOptions}
                    onChange={(e: any) => {
                      handleAliasProgramChange(e, true);
                    }}
                    defaultValue={""}
                    onBlur={programsReg.onBlur}
                    innerRef={programsReg.ref}
                    style={{ width: "inherit" }}
                  />
                </>
              </FlexGrow>
            </FlexBox>
            {aliasCourseOptions?.length > 0 && (
              <FlexBox
                alignItems="center"
                style={{
                  marginBottom: theme.spacing.x2,
                }}
              >
                <FlexGrow grow={1}>
                  <StyledText color={"#000000"} fontWeight={"bold"}>
                    Alias Course Code
                  </StyledText>
                </FlexGrow>
                <FlexGrow grow={2}>
                  <>
                    <StyledMinimalSelect
                      // multi
                      value={
                        aliasCourseOptions.find(
                          (v: any) =>
                            v.value === newAliasData.course_master_alias_id
                        ) || null
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                      }}
                      placeholder="Find Courses..."
                      options={aliasCourseOptions}
                      onChange={(e: any) => {
                        setNewAliasData({
                          ...newAliasData,
                          course_master_alias_id: e.value,
                        });
                      }}
                      defaultValue={""}
                      style={{ width: "inherit" }}
                    />
                  </>
                </FlexGrow>
              </FlexBox>
            )}

            <FlexBox
              alignItems="center"
              justify="flex-end"
              style={{
                marginTop: theme.spacing.x4,
                marginBottom: theme.spacing.x2,
              }}
            >
              <FlexGrow>
                <RightAlign>
                  <Button
                    onClick={handleAddAlias}
                    disabled={
                      !(
                        newAliasData?.source_program_id &&
                        newAliasData?.alias_program_id &&
                        newAliasData?.course_master_alias_id &&
                        newAliasData?.course_master_id
                      )
                    }
                    status={Status.primary}
                    backgroundColor="#CB092B"
                    style={{ padding: "4px 12px", marginRight: "8px" }}
                  >
                    <Icon name="plus" mr={0.5} />
                    Add Alias
                  </Button>
                  <Button
                    onClick={() => {
                      setOpen?.(false);
                    }}
                    isOutline
                    status={Status.primary}
                    style={{ padding: "4px 30px" }}
                  >
                    {/* <Icon name="x-s" mr={0.5} /> */}
                    Done
                  </Button>
                </RightAlign>
              </FlexGrow>
            </FlexBox>
          </form>
        ) : (
          <Icon mr={1} name="spinner" spin />
        )}
        <>
          <FlexBox>
            <h3>Aliases</h3>{" "}
            
          </FlexBox>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            <Table>
              <thead
                style={{
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 1,
                }}
              >
                <tr>
                  <th>Program</th>
                  <th>Alias Course</th>
                  <th>Alias Program</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    <Icon mr={1} name="spinner" spin />
                    Loading...
                  </>
                ) : (
                  <>
                    {currentAliasSection}
                    {!aliasData && <NullStateTR>No sections.</NullStateTR>}
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </>
      </Modal>
    </>
  );
};

export default AliasEditModal;
